import React, { useState, useEffect } from 'react';
import DrawerDetails from '../utils/drawer';
import { Button, Box, Pagination, Typography, Grid } from '@barracuda-internal/bds-core';
import { SmallSeverityBars } from '../utils/utility';
import { getSeverityText } from '../utils/color';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';

export default function Vulnerabilities({
  state,
  vulnerabilities,
  url,
  title,
  subtitle,
  isSafe,
  complianceFilter,
  setComplianceFilter
}) {



  // -------------------------
  //  APPLY FILTER
  // -------------------------
  let filteredVulnerabilities = vulnerabilities;

  if (complianceFilter) {
    const rawIds = complianceFilter.ids;

    // Normalize invalid ids → [] always
    const ids = Array.isArray(rawIds)
      ? rawIds.filter(id => typeof id === "string" && id.trim() !== "")
      : [];

    

    // If no valid ids → show nothing
    if (ids.length === 0) {
      filteredVulnerabilities = [];
    } else {
      filteredVulnerabilities = vulnerabilities.filter(v =>
        ids.includes(v.name)
      );
    }
  }

  // -------------------------
  //  PAGINATION SETUP
  // -------------------------
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1); // reset page on filter change
  }, [complianceFilter]);

  // -------------------------
  // Pagination calculations
  // -------------------------
  const totalCount = filteredVulnerabilities.length;

  const backendPageSize = Number(
    state?.scanResult?.data?.page_size ??
    state?.scanResult?.data?.per_page ??
    state?.scanResult?.data?.pageSize ??
    10
  );

  const itemsPerPage =
    Number.isFinite(backendPageSize) && backendPageSize > 0
      ? backendPageSize
      : 10;

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalCount);

  const pageItems = filteredVulnerabilities.slice(startIndex, endIndex);

  function goto(pageNum) {
    const p = Math.max(1, Math.min(totalPages, pageNum));
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // -------------------------
  // UI RENDER
  // -------------------------
  return (
    <Box sx={{ p: 2 }}>

      <Typography variant="h5" gutterBottom>
        {title} ({totalCount})
      </Typography>

      {subtitle && (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
    <Typography variant="promoCaptionSubtle">
      {subtitle}
    </Typography>

    {complianceFilter && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          background: "#1976d2",     // BLUE
          color: "white",            // WHITE TEXT
          borderRadius: "16px",
          padding: "4px 10px",
          fontSize: "12px",
          fontWeight: 600,
          whiteSpace: "nowrap",
          width: "fit-content",
        }}
      >
        {complianceFilter.type.toUpperCase()}

        <span
          style={{
            marginLeft: "8px",
            cursor: "pointer",
            fontWeight: 900,
          }}
          onClick={() => setComplianceFilter(null)}
        >
          ✕
        </span>
      </Box>
    )}
  </Box>
)}

      <Box sx={{ mt: 2 }}>
        {pageItems.map((v, idx) => (
          <React.Fragment key={v.id ?? `${v.name}-${idx}`}>
            <Grid
              container
              alignItems="center"
              sx={{
                boxShadow: 0,
                borderRadius: 2,
                mb: 1,
              }}
            >
              {/* Status Pill */}
              <Grid item xs={7}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    py: 0.5,
                    borderRadius: 2,
                    gap: 1,
                    color: isSafe ? '#2e7d32' : '#d32f2f',
                    fontWeight: 700
                  }}
                >
                  {isSafe ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 1.2,
                        py: 0.4,
                        borderRadius: '10px',
                        background: 'linear-gradient(180deg, #e8f5e9, #d7efe0)',
                        color: '#2e7d32',
                        fontWeight: 600,
                        gap: 0.5,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                      }}
                    >
                      <ShieldOutlinedIcon sx={{ fontSize: 14 }} />
                      Safe
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 1.2,
                        py: 0.4,
                        borderRadius: '10px',
                        background: 'linear-gradient(90deg, #ffebee 60%)',
                        color: '#d32f2f',
                        fontWeight: 600,
                        gap: 0.5,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                      }}
                    >
                      <ReportProblemRoundedIcon sx={{ fontSize: 18 }} />
                      Risk
                    </Box>
                  )}

                  <Typography sx={{ fontWeight: 600 }}>
                    {v.attack_type_title || v.name}
                  </Typography>
                </Box>
              </Grid>

              {/* Severity */}
              <Grid item xs={2}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Severity: {getSeverityText(v.severity_level).toLowerCase()}
                  </Typography>
                  <SmallSeverityBars severity={v.severity_level} />
                </Box>
              </Grid>

              {/* View details */}
              <Grid item xs={3} sx={{ textAlign: 'right' }}>
                <Button
                  bdsType="interactiveSubtle"
                  onClick={() => {
                    setSelected(v);
                    setDrawerOpen(true);
                  }}
                >
                  View details
                </Button>
              </Grid>
            </Grid>
          </React.Fragment>
        ))}
      </Box>

      {/* Pagination */}
      <Box sx={{ display: 'flex', mt: 2 }}>
        <Pagination
          itemsPerPage={itemsPerPage}
          page={page}
          totalItems={totalCount}
          onChange={(e, p) => goto(p)}
        />
      </Box>

      {/* Drawer */}
      <DrawerDetails
        url={url}
        open={drawerOpen}
        selected={selected}
        onClose={() => {
          setDrawerOpen(false);
          setSelected(null);
        }}
        isSafe={isSafe}
      />
    </Box>
  );
}